import { motion } from "framer-motion";
import {
  MessageSquareHeart,
  Eye,
  Workflow,
  Wallet,
  Handshake,
  Sparkles,
  Star,
} from "lucide-react";

const points = [
  { icon: MessageSquareHeart, title: "Direct communication", desc: "From first message to final toast—always you and me." },
  { icon: Eye, title: "Personal attention", desc: "Every detail is considered, refined, and personally checked." },
  { icon: Sparkles, title: "Tailored planning", desc: "No templates. Each event is shaped uniquely around you." },
  { icon: Wallet, title: "Flexible solutions", desc: "Thoughtful options that respect your style and budget." },
  { icon: Handshake, title: "Trusted vendors", desc: "A handpicked network of artisans known for excellence." },
  { icon: Workflow, title: "Smooth execution", desc: "Calm coordination on the day—so you stay in the moment." },
  { icon: Star, title: "Creative + organised", desc: "Imagination paired with rigorous, methodical delivery." },
];

export default function WhyChooseUs() {
  return (
    <section id="why" className="relative section-pad" data-testid="why-section">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-gold/[0.04] blur-[120px] rounded-full" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">
              The Shree Difference
            </span>
            <span className="h-px w-10 bg-gold" />
          </div>
          <h2
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="why-heading"
          >
            Why choose <span className="italic text-gold-gradient">Shree</span>.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {points.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                className="glass rounded-2xl p-6 flex gap-5 items-start hover:border-gold/30 transition-colors duration-500 relative group"
                data-testid={`why-point-${i}`}
              >
                <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-gold/30 to-gold/5 border border-gold/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Icon size={20} className="text-gold" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-ivory mb-1.5">{p.title}</h3>
                  <p className="text-ivory/60 text-sm leading-relaxed font-light">{p.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
